import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { EditRoleSettingsForUserDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  userGuid: string;
  roleSettings: {
    roleGuid: string;
    isPrimaryRole: boolean;
    isEngagementLocked: boolean;
    engagementPercentage: number;
  }[];
};

const useEditRoleSettingsForUserRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editRoleSettingsForUserRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditRoleSettingsForUserDto = {
      roleSettings: variables.roleSettings.map((engagement) => ({
        roleGuid: engagement.roleGuid,
        isPrimaryRole: engagement.isPrimaryRole,
        isEngagementLocked: engagement.isEngagementLocked,
        engagementPercentage: engagement.engagementPercentage,
      })),
    };

    const response = await fetch(`${configuration.apiRootUrl}/users/${variables.userGuid}/role-settings`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editRoleSettingsForUserRequest };
};

const useEditRoleSettingsForUser = () => {
  const { editRoleSettingsForUserRequest } = useEditRoleSettingsForUserRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editRoleSettingsForUserRequest,
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries({ queryKey: queryKeys.roles.getRolesForUser(variables.userGuid) });
    },
  });
};

export { useEditRoleSettingsForUser };
